import { memo, PropsWithChildren, ComponentProps, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@ifixit/ui/components/popover';
import { cn } from '@ifixit/ui/lib/utils';

type VerticalContentsWrapperProps = ComponentProps<'div'>;

function VerticalContentsWrapper({ className, ...props }: VerticalContentsWrapperProps) {
   return (
      <div
         className={cn(
            'flex flex-col items-start p-3 md:p-4 max-h-[50vh] overflow-y-auto overflow-x-hidden',
            className
         )}
         {...props}
      />
   );
}

type GeneralTooltipContentProps = {
   maxWidth?: string;
   className?: string;
   zIndex?: number;
};

export type GeneralTooltipProps = PropsWithChildren<{
   content: JSX.Element | string;
   contentProps?: GeneralTooltipContentProps;
   appendTo?: () => Element | null;
   isOpen?: boolean;
   defaultIsOpen?: boolean;
   onOpen?: () => void;
   onClose?: () => void;
   isLazy?: boolean;
   closeOnBlur?: boolean;
   autoFocus?: boolean;
   returnFocusOnClose?: boolean;
   closeOnEsc?: boolean;
}>;

const GeneralTooltip = ({
   children,
   content,
   contentProps,
   appendTo,
   isOpen,
   defaultIsOpen,
   onOpen,
   onClose,
   isLazy,
   closeOnBlur = true,
   autoFocus = true,
}: GeneralTooltipProps) => {
   const attachTo = appendTo ? appendTo() : null;

   const handleOpenChange = (open: boolean) => {
      if (open) {
         onOpen?.();
      } else {
         onClose?.();
      }
   };

   const { maxWidth, className: contentClassName, zIndex = 9999 } = contentProps || {};

   const tooltipContentElement = (
      <PopoverContent
         className={cn(
            'text-black bg-white rounded-md border-0 p-0 shadow-2xl w-auto min-w-0 overflow-visible z-[9999]',
            contentClassName
         )}
         style={{ maxWidth, zIndex }}
         // 5px of gap plus the 5px arrow, which Radix added to the offset.
         sideOffset={10}
         onOpenAutoFocus={(e: Event) => {
            if (!autoFocus) e.preventDefault();
         }}
         onInteractOutside={(e: Event) => {
            if (!closeOnBlur) e.preventDefault();
         }}
      >
         {content}
         <span
            aria-hidden
            className="absolute left-(--popover-anchor-x) -translate-x-1/2 border-x-[5px] border-x-transparent in-data-[side=bottom]:bottom-full in-data-[side=bottom]:border-b-[5px] in-data-[side=bottom]:border-b-white in-data-[side=top]:top-full in-data-[side=top]:border-t-[5px] in-data-[side=top]:border-t-white"
         />
      </PopoverContent>
   );

   // Don't render content if lazy and not open
   const shouldRenderContent = !isLazy || isOpen;

   return (
      <Popover open={isOpen} defaultOpen={defaultIsOpen} onOpenChange={handleOpenChange}>
         <PopoverTrigger asChild>{children}</PopoverTrigger>
         {/* An SVG hotspot can't hold the HTML content, so it names a container. */}
         {shouldRenderContent &&
            (attachTo ? createPortal(tooltipContentElement, attachTo) : tooltipContentElement)}
      </Popover>
   );
};

export default memo(GeneralTooltip);
export { VerticalContentsWrapper };
